/**
 * Logo - Clara brand lockup
 *
 * Single source of truth for the three-circle mark + "Clara" wordmark, shared by
 * AppLayout and every auth/pricing page instead of being copy-pasted per screen.
 * The mark is decorative and marked `aria-hidden`, so the link's accessible name
 * is exactly "Clara" — this is what stops screen readers from announcing the glyph
 * and the word separately (the double-read the mobile a11y audit in #259 flagged
 * on the old "R" + "Reconflux" lockup).
 *
 * The three overlapping circles (yellow / blue / red-orange) use literal brand
 * hexes on purpose: the mark is theme-invariant, like a raster logo would be, and
 * reads the same on both the dark and light page surfaces. The "Clara" wordmark,
 * by contrast, uses the semantic `text-accent` token so it tracks the brand accent
 * per theme.
 *
 * Logged-out pages pass `href` to link the mark out to the marketing landing
 * page instead of an in-app route (issue #522).
 */

import { Link } from 'react-router-dom'

export function Logo({
  to = '/dashboard',
  href,
  className = '',
}: {
  /** In-app link destination (logged-in shell default). Ignored if `href` is set. */
  to?: string
  /** External URL to link to instead of an in-app route; wins over `to` if both are given. */
  href?: string
  /** Extra classes on the link wrapper (e.g. `shrink-0`). */
  className?: string
}) {
  const mark = (
    <>
      <svg
        aria-hidden="true"
        viewBox="0 0 32 32"
        className="w-8 h-8 shrink-0"
      >
        <circle cx="12.8" cy="12.2" r="7.7" fill="#F2C300" fillOpacity="0.75" />
        <circle cx="19.8" cy="12.8" r="7.7" fill="#4AA8F0" fillOpacity="0.75" />
        <circle cx="16" cy="19.8" r="7.7" fill="#F15A24" fillOpacity="0.75" />
      </svg>
      <span className="text-accent font-semibold">Clara</span>
    </>
  )

  if (href) {
    return (
      <a href={href} className={`inline-flex items-center gap-2 ${className}`.trim()}>
        {mark}
      </a>
    )
  }

  return (
    <Link to={to} className={`inline-flex items-center gap-2 ${className}`.trim()}>
      {mark}
    </Link>
  )
}
